// CPU AI Module for TopGammon
// Handles computer opponent logic with 5 difficulty levels based on GTO accuracy

// Difficulty levels with accuracy percentages
const DIFFICULTY_LEVELS = {
  1: { name: 'Beginner', description: '35% GTO accuracy - Makes basic moves, sometimes misses opportunities' },
  2: { name: 'Novice', description: '50% GTO accuracy - Plays conservatively, avoids obvious mistakes' },
  3: { name: 'Intermediate', description: '65% GTO accuracy - Balanced play, considers basic strategy' },
  4: { name: 'Advanced', description: '80% GTO accuracy - Strategic play, considers multiple moves ahead' },
  5: { name: 'Expert', description: '100% GTO accuracy - Perfect play, very hard to beat' }
};

// Helper function to calculate pip count for a player
function calculatePipCount(checkers, player, bar, borneOff) {
  let total = 0;
  for (let c of checkers) {
    if (c.player === player) {
      total += player === 1 ? 24 - c.point : c.point + 1;
    }
  }
  // Bar checkers
  total += bar[player].length * 25;
  // Borne off checkers are 0
  return total;
}

// Helper function to check if a player can bear off
function canBearOff(checkers, player, bar) {
  let homeQuadrant = player === 1 ? [18, 19, 20, 21, 22, 23] : [0, 1, 2, 3, 4, 5];
  let checkersOutside = checkers.reduce((sum, c) => {
    if (c.player === player && !homeQuadrant.includes(c.point)) sum++;
    return sum;
  }, 0) + bar[player].length;
  return checkersOutside === 0;
}

// Advanced position evaluation based on GTO principles
function evaluatePosition(checkers, bar, borneOff, player) {
  let score = 0;
  const opponent = 3 - player;
  
  // Pip count (lower is better) - weighted heavily
  let playerPipCount = calculatePipCount(checkers, player, bar, borneOff);
  let opponentPipCount = calculatePipCount(checkers, opponent, bar, borneOff);
  score -= playerPipCount * 0.15;
  score += opponentPipCount * 0.15; // Opponent's pips help us
  
  // Borne off checkers (higher is better) - very important
  score += borneOff[player] * 15;
  score -= borneOff[opponent] * 15;
  
  // Bar checkers (lower is better) - very bad
  score -= bar[player].length * 25;
  score += bar[opponent].length * 25;
  
  // Checker distribution analysis
  let playerCheckers = checkers.filter(c => c.player === player);
  let opponentCheckers = checkers.filter(c => c.player === opponent);
  let homeQuadrant = player === 1 ? [18, 19, 20, 21, 22, 23] : [0, 1, 2, 3, 4, 5];
  let opponentHomeQuadrant = player === 1 ? [0, 1, 2, 3, 4, 5] : [18, 19, 20, 21, 22, 23];
  
  // Home board control (very important)
  let homeCheckers = playerCheckers.filter(c => homeQuadrant.includes(c.point));
  let opponentHomeCheckers = opponentCheckers.filter(c => opponentHomeQuadrant.includes(c.point));
  score += homeCheckers.length * 3;
  score -= opponentHomeCheckers.length * 3;
  
  // Near bear-off checkers (important for endgame)
  let nearBearOff = playerCheckers.filter(c => {
    let distance = player === 1 ? 24 - c.point : c.point + 1;
    return distance <= 6;
  });
  score += nearBearOff.length * 4;
  
  // Blots (single checkers) - very bad
  let playerBlots = playerCheckers.filter(c => {
    let pointCheckers = checkers.filter(cc => cc.point === c.point);
    return pointCheckers.length === 1;
  });
  let opponentBlots = opponentCheckers.filter(c => {
    let pointCheckers = checkers.filter(cc => cc.point === c.point);
    return pointCheckers.length === 1;
  });
  score -= playerBlots.length * 8;
  score += opponentBlots.length * 8;
  
  // Anchors (2+ checkers on opponent's home points) - very good
  let anchors = opponentHomeQuadrant.filter(point => {
    let pointCheckers = checkers.filter(c => c.point === point && c.player === player);
    return pointCheckers.length >= 2;
  });
  score += anchors.length * 12;
  
  // Prime building (consecutive points with 2+ checkers)
  let primeLength = calculatePrimeLength(checkers, player);
  score += primeLength * 6;
  
  // Back game evaluation
  if (isBackGame(checkers, player, bar)) {
    score += evaluateBackGame(checkers, player, bar, borneOff);
  }
  
  // Race vs contact evaluation
  if (isRace(checkers, bar, borneOff)) {
    score += evaluateRace(checkers, bar, borneOff, player);
  }
  
  return score;
}

// Calculate prime length (consecutive points with 2+ checkers)
function calculatePrimeLength(checkers, player) {
  let maxLength = 0;
  let currentLength = 0;
  
  for (let point = 0; point < 24; point++) {
    let pointCheckers = checkers.filter(c => c.point === point && c.player === player);
    if (pointCheckers.length >= 2) {
      currentLength++;
      maxLength = Math.max(maxLength, currentLength);
    } else {
      currentLength = 0;
    }
  }
  
  return maxLength;
}

// Check if this is a back game
function isBackGame(checkers, player, bar) {
  let homeQuadrant = player === 1 ? [18, 19, 20, 21, 22, 23] : [0, 1, 2, 3, 4, 5];
  let playerCheckers = checkers.filter(c => c.player === player);
  let homeCheckers = playerCheckers.filter(c => homeQuadrant.includes(c.point));
  
  // Back game if most checkers are not in home quadrant
  return homeCheckers.length < playerCheckers.length / 2;
}

// Check if this is a race (both players can bear off)
function isRace(checkers, bar, borneOff) {
  return canBearOff(checkers, 1, bar) && canBearOff(checkers, 2, bar);
}

// Evaluate back game position
function evaluateBackGame(checkers, player, bar, borneOff) {
  let score = 0;
  const opponent = 3 - player;
  
  // In back game, we want to keep opponent's checkers on the bar
  score += bar[opponent].length * 10;
  
  // We want to have anchors in opponent's home board
  let opponentHome = player === 1 ? [0, 1, 2, 3, 4, 5] : [18, 19, 20, 21, 22, 23];
  let anchors = opponentHome.filter(point => {
    let pointCheckers = checkers.filter(c => c.point === point && c.player === player);
    return pointCheckers.length >= 2;
  });
  score += anchors.length * 15;
  
  return score;
}

// Evaluate race position
function evaluateRace(checkers, bar, borneOff, player) {
  let score = 0;
  const opponent = 3 - player;
  
  // In race, pip count is everything
  let playerPipCount = calculatePipCount(checkers, player, bar, borneOff);
  let opponentPipCount = calculatePipCount(checkers, opponent, bar, borneOff);
  
  // Crawford rule: if opponent is on 1, we get a bonus
  if (borneOff[opponent] === 14) {
    score += 50;
  }
  
  // If we're ahead by more than 10%, we're winning
  let pipDifference = opponentPipCount - playerPipCount;
  if (pipDifference > 10) {
    score += 30;
  }
  
  return score;
}

// Generate all legal moves for a position
function generateLegalMoves(checkers, bar, borneOff, movesAllowed, usedDice, player) {
  let moves = [];
  let availableDice = movesAllowed.filter((d, i) => !usedDice.includes(i));
  
  // Bar entry moves
  if (bar[player].length > 0) {
    for (let i = 0; i < availableDice.length; i++) {
      let die = availableDice[i];
      let entryPoint = player === 1 ? die - 1 : 24 - die;
      
      if (entryPoint >= 0 && entryPoint <= 23) {
        let pointCheckers = checkers.filter(c => c.point === entryPoint);
        if (pointCheckers.length === 0 || pointCheckers[0].player === player || pointCheckers.length === 1) {
          moves.push({
            type: 'bar_entry',
            from: 'bar',
            to: entryPoint,
            die: die,
            dieIndex: i
          });
        }
      }
    }
  }
  
  // Regular moves
  let playerCheckers = checkers.filter(c => c.player === player);
  for (let checker of playerCheckers) {
    let from = checker.point;
    
    for (let i = 0; i < availableDice.length; i++) {
      let die = availableDice[i];
      let to = player === 1 ? from + die : from - die;
      
      if (to >= 0 && to <= 23) {
        let pointCheckers = checkers.filter(c => c.point === to);
        if (pointCheckers.length === 0 || pointCheckers[0].player === player || pointCheckers.length === 1) {
          moves.push({
            type: 'regular',
            from: from,
            to: to,
            die: die,
            dieIndex: i,
            checker: checker
          });
        }
      }
    }
  }
  
  // Bearing off moves
  if (canBearOff(checkers, player, bar)) {
    let homeQuadrant = player === 1 ? [18, 19, 20, 21, 22, 23] : [0, 1, 2, 3, 4, 5];
    let homeCheckers = playerCheckers.filter(c => homeQuadrant.includes(c.point));
    
    for (let checker of homeCheckers) {
      let distance = player === 1 ? 24 - checker.point : checker.point + 1;
      
      for (let i = 0; i < availableDice.length; i++) {
        let die = availableDice[i];
        
        // Check if this checker can bear off
        let canBearOffThisChecker = false;
        if (die === distance) {
          canBearOffThisChecker = true;
        } else if (die > distance) {
          // Can only bear off with higher die if it's the farthest checker
          let farthestDistance = Math.max(...homeCheckers.map(c => {
            return player === 1 ? 24 - c.point : c.point + 1;
          }));
          if (distance === farthestDistance) {
            canBearOffThisChecker = true;
          }
        }
        
        if (canBearOffThisChecker) {
          moves.push({
            type: 'bear_off',
            from: checker.point,
            die: die,
            dieIndex: i,
            checker: checker
          });
        }
      }
    }
  }
  
  return moves;
}

// Evaluate a single move
function evaluateMove(move, checkers, bar, borneOff, player) {
  // Create a copy of the game state to simulate the move
  let newCheckers = JSON.parse(JSON.stringify(checkers));
  let newBar = JSON.parse(JSON.stringify(bar));
  let newBorneOff = { ...borneOff };
  
  // Simulate the move
  if (move.type === 'bar_entry') {
    // Remove checker from bar and add to entry point
    let barChecker = newBar[player].pop();
    let newChecker = { ...barChecker, point: move.to };
    newCheckers.push(newChecker);
    
    // Handle hit if any
    let pointCheckers = newCheckers.filter(c => c.point === move.to);
    if (pointCheckers.length > 1) {
      let hitChecker = pointCheckers.find(c => c.player !== player);
      if (hitChecker) {
        let hitBarChecker = { ...hitChecker, point: hitChecker.player === 1 ? 24 : -1 };
        newBar[hitChecker.player].push(hitBarChecker);
        newCheckers = newCheckers.filter(c => c.id !== hitChecker.id);
      }
    }
  } else if (move.type === 'regular') {
    // Move checker
    let checker = newCheckers.find(c => c.id === move.checker.id);
    checker.point = move.to;
    
    // Handle hit if any
    let pointCheckers = newCheckers.filter(c => c.point === move.to);
    if (pointCheckers.length > 1) {
      let hitChecker = pointCheckers.find(c => c.player !== player);
      if (hitChecker) {
        let hitBarChecker = { ...hitChecker, point: hitChecker.player === 1 ? 24 : -1 };
        newBar[hitChecker.player].push(hitBarChecker);
        newCheckers = newCheckers.filter(c => c.id !== hitChecker.id);
      }
    }
  } else if (move.type === 'bear_off') {
    // Remove checker and increment borne off
    newCheckers = newCheckers.filter(c => c.id !== move.checker.id);
    newBorneOff[player]++;
  }
  
  // Evaluate the resulting position
  return evaluatePosition(newCheckers, newBar, newBorneOff, player);
}

// Get the best move based on difficulty level
function getBestMove(checkers, bar, borneOff, movesAllowed, usedDice, player, difficulty) {
  let legalMoves = generateLegalMoves(checkers, bar, borneOff, movesAllowed, usedDice, player);
  
  if (legalMoves.length === 0) {
    return null; // No legal moves
  }
  
  // Evaluate all moves
  let evaluatedMoves = legalMoves.map(move => ({
    ...move,
    score: evaluateMove(move, checkers, bar, borneOff, player)
  }));
  
  // Sort by score (best first)
  evaluatedMoves.sort((a, b) => b.score - a.score);
  
  // Apply difficulty-based selection
  let selectedMove;
  
  if (difficulty === 5) {
    // 100% GTO - always pick the best move
    selectedMove = evaluatedMoves[0];
  } else {
    // For lower difficulties, introduce randomness based on accuracy
    let accuracy = [0.35, 0.50, 0.65, 0.80][difficulty - 1];
    let random = Math.random();
    
    if (random < accuracy) {
      // Pick the best move
      selectedMove = evaluatedMoves[0];
    } else {
      // Pick a random move from the top 50% of moves
      let topMoves = evaluatedMoves.slice(0, Math.ceil(evaluatedMoves.length * 0.5));
      let randomIndex = Math.floor(Math.random() * topMoves.length);
      selectedMove = topMoves[randomIndex];
    }
  }
  
  return selectedMove;
}

// Main CPU AI function
function cpuMakeMove(gameState, difficulty) {
  const { checkers, bar, borneOff, movesAllowed, usedDice, player } = gameState;
  
  let bestMove = getBestMove(checkers, bar, borneOff, movesAllowed, usedDice, player, difficulty);
  
  if (!bestMove) {
    return { type: 'no_moves' };
  }
  
  return {
    type: 'move',
    move: bestMove,
    difficulty: difficulty,
    accuracy: [0.35, 0.50, 0.65, 0.80, 1.0][difficulty - 1]
  };
}

// Calculate move accuracy for human players (for post-game analysis)
function calculateMoveAccuracy(move, checkers, bar, borneOff, movesAllowed, usedDice, player) {
  let legalMoves = generateLegalMoves(checkers, bar, borneOff, movesAllowed, usedDice, player);
  let evaluatedMoves = legalMoves.map(m => ({
    ...m,
    score: evaluateMove(m, checkers, bar, borneOff, player)
  }));
  
  evaluatedMoves.sort((a, b) => b.score - a.score);
  
  // Find the rank of the actual move
  let moveRank = evaluatedMoves.findIndex(m => 
    m.type === move.type && 
    m.from === move.from && 
    m.to === move.to
  );
  
  if (moveRank === -1) return 0; // Invalid move
  
  // Calculate accuracy based on rank
  let totalMoves = evaluatedMoves.length;
  let accuracy = (totalMoves - moveRank) / totalMoves;
  
  return Math.max(0, Math.min(1, accuracy));
}

// Calculate winning probability for a player
function calculateWinningProbability(checkers, bar, borneOff, player) {
  const opponent = 3 - player;
  
  // If either player has won, return 1 or 0
  if (borneOff[player] === 15) return 1.0;
  if (borneOff[opponent] === 15) return 0.0;
  
  // Calculate pip counts
  const playerPipCount = calculatePipCount(checkers, player, bar, borneOff);
  const opponentPipCount = calculatePipCount(checkers, opponent, bar, borneOff);
  
  // Calculate borne off checkers
  const playerBorneOff = borneOff[player];
  const opponentBorneOff = borneOff[opponent];
  
  // Calculate bar checkers
  const playerBarCheckers = bar[player].length;
  const opponentBarCheckers = bar[opponent].length;
  
  // Base probability calculation
  let probability = 0.5; // Start at 50%
  
  // Pip count difference (most important factor)
  const pipDifference = opponentPipCount - playerPipCount;
  if (pipDifference > 0) {
    // Player is ahead in race
    probability += Math.min(0.3, pipDifference * 0.01); // Max 30% bonus
  } else {
    // Player is behind in race
    probability -= Math.min(0.3, Math.abs(pipDifference) * 0.01); // Max 30% penalty
  }
  
  // Borne off checkers difference
  const borneOffDifference = playerBorneOff - opponentBorneOff;
  probability += borneOffDifference * 0.02; // Each borne off checker = 2%
  
  // Bar checkers difference (very important)
  const barDifference = opponentBarCheckers - playerBarCheckers;
  probability += barDifference * 0.05; // Each bar checker = 5%
  
  // Position evaluation bonus
  const positionScore = evaluatePosition(checkers, bar, borneOff, player);
  const opponentScore = evaluatePosition(checkers, bar, borneOff, opponent);
  const scoreDifference = positionScore - opponentScore;
  
  // Convert score difference to probability (normalize)
  probability += Math.max(-0.2, Math.min(0.2, scoreDifference * 0.001)); // Max 20% bonus/penalty
  
  // Ensure probability is between 0 and 1
  return Math.max(0, Math.min(1, probability));
}

// Export the CPU AI functions
export {
  DIFFICULTY_LEVELS,
  cpuMakeMove,
  getBestMove,
  evaluatePosition,
  calculatePipCount,
  canBearOff,
  generateLegalMoves,
  evaluateMove,
  calculateMoveAccuracy,
  calculateWinningProbability
}; 